/**
 * Core features that are supported by all native embeds.
 *
 * All native embed must either support the feature or respond in
 * the feature handshake that its not supported.
 */
export const NativeEmbedCoreFeature = {
	/** When agreed, the parent sends selected-change events to the child when the embed's selection state changes. */
	SelectedChange: 'selected-change',
	/** When agreed, the parent sends view-mode-change events to the child when the embed switches between regular and maximize (e.g. full screen). */
	ViewModeChange: 'view-mode-change',
	/** When agreed, the parent sends header-visibility-change events to the child when the host native embed controls/chrome visibility changes. */
	HeaderVisibilityChange: 'header-visibility-change',
	/** When agreed, the child captures click events and notifies the host via the bridge. */
	NotifyHostClick: 'notify-host-click',
	/** When agreed, the child can request the host to toggle the full-screen dialog. */
	ToggleFullScreen: 'toggle-full-screen',
	/**
	 * When agreed, the child can request the host to resize the embed container. */
	RequestResize: 'request-resize',
	/**
	 * When agreed, the host sends `on-full-screen-changed` events to the child whenever
	 * the inline full-screen preview opens or closes WITHOUT remounting the iframe.
	 *
	 * This handshake is only ever negotiated when the host runs the shared-iframe
	 * code path (i.e. `Element.prototype.moveBefore` is supported AND the
	 * `platform_native_embeds_full_rollout_bugfix` gate is on). On any other
	 * host path the embed is torn down and re-created across the full-screen
	 * toggle, so a child has no need for this event.
	 */
	NoRefreshFullScreenPreview: 'no-refresh-full-screen-preview',
	/**
	 * When agreed, the child sends a `content-ready` event to the host once its
	 * content is fully rendered and visible to the user. The host holds its own
	 * loading overlay visible until this event fires (or a safety timeout elapses),
	 * preventing a flash of the child's internal loading state between the iframe
	 * `load` event and the child's first painted frame.
	 *
	 * Only negotiated for embed types that set `waitForContentReady: true` in their
	 * `uiConfig` (currently database native embeds).
	 */
	ContentReady: 'content-ready',
} as const;

/** All core feature values as an array, for use in supportedFeatures sets. */
export const allNativeEmbedCoreFeatures = Object.values(
	NativeEmbedCoreFeature,
) as (typeof NativeEmbedCoreFeature)[keyof typeof NativeEmbedCoreFeature][];

/**
 * Non-core features that are supported by some native embeds.
 */
export type NativeEmbedNonCoreFeature = string;

export type NativeEmbedFeature =
	| (typeof NativeEmbedCoreFeature)[keyof typeof NativeEmbedCoreFeature]
	| NativeEmbedNonCoreFeature;
